import { inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  defer,
  finalize,
  from,
  Observable,
  of,
  shareReplay,
  switchMap,
  throwError,
} from 'rxjs';

// 3rd party
import { decodeJwt } from 'jose';

import {
  CognitoIdentityProviderClient,
  CognitoIdentityProviderClientConfig,
  SignUpCommand,
  InitiateAuthCommand,
  ResendConfirmationCodeCommand,
  GetUserCommand,
  ConfirmSignUpCommand,
  AuthFlowType,
  ForgotPasswordCommand,
  AttributeType,
  AuthenticationResultType,
  ConfirmForgotPasswordCommand,
  RespondToAuthChallengeCommand,
} from '@aws-sdk/client-cognito-identity-provider';
import { ConfigService } from '../services/config';
import { UserTypeEnum } from '../enums/auth';
import {
  AuthUser,
  TermsAndConditionsChecksum,
  TermsAndConditionsPresignedUrl,
  TermsAndConditionsResponse,
} from '../models/auth';
import { AUTH_STORAGE_KEYS } from '../constants/auth-storage';
import type { InitiateAuthCommandOutput } from '@aws-sdk/client-cognito-identity-provider';
import { isTokenExpiringSoon } from '../helpers/auth-session';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  readonly http = inject(HttpClient);
  private readonly configService = inject(ConfigService);
  private readonly config: CognitoIdentityProviderClientConfig = {
    region: this.configService.region,
  };
  private readonly client = new CognitoIdentityProviderClient(this.config);

  private static refreshInFlight$:
    | Observable<InitiateAuthCommandOutput>
    | null = null;

  readonly newPasswordChallengSession = signal<string | undefined>(undefined);

  setNewPasswordChallengeSession(session: string | undefined) {
    this.newPasswordChallengSession.set(session);
  }

  login(username: string, password: string) {
    const command = new InitiateAuthCommand({
      ClientId: this.configService.clientId,
      AuthFlow: AuthFlowType.USER_PASSWORD_AUTH,
      AuthParameters: {
        USERNAME: username,
        PASSWORD: password,
      },
    });
    return from(this.client.send(command));
  }

  forgotPassword(email: string) {
    const command = new ForgotPasswordCommand({
      ClientId: this.configService.clientId,
      Username: email,
    });
    return from(this.client.send(command));
  }

  confirmPasswordReset(
    email: string,
    verificationCode: string,
    newPassword: string,
  ) {
    const command = new ConfirmForgotPasswordCommand({
      ClientId: this.configService.clientId,
      Username: email,
      ConfirmationCode: verificationCode,
      Password: newPassword,
    });

    return from(this.client.send(command));
  }

  resendConfirmation(email: string) {
    const command = new ResendConfirmationCodeCommand({
      ClientId: this.configService.clientId,
      Username: email,
    });
    return from(this.client.send(command));
  }

  /** Refresh session using the stored refresh token (deduped while in flight). */
  refreshSession(): Observable<InitiateAuthCommandOutput> {
    if (!AuthService.refreshInFlight$) {
      AuthService.refreshInFlight$ = defer(() => {
        const refreshToken =
          localStorage.getItem(AUTH_STORAGE_KEYS.refreshToken) ?? '';
        if (!refreshToken) {
          return throwError(() => new Error('No refresh token'));
        }
        const command = new InitiateAuthCommand({
          ClientId: this.configService.clientId,
          AuthFlow: AuthFlowType.REFRESH_TOKEN_AUTH,
          AuthParameters: { REFRESH_TOKEN: refreshToken },
        });
        return from(this.client.send(command));
      }).pipe(
        finalize(() => {
          AuthService.refreshInFlight$ = null;
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
      );
    }
    return AuthService.refreshInFlight$;
  }

  isTokenValid(token: string): boolean {
    try {
      const { exp } = decodeJwt(token);
      if (!exp) return false;

      const expiryTime = exp * 1000;
      return Date.now() < expiryTime;
    } catch {
      return false;
    }
  }

  /** True when id or access token is missing, invalid, or within the refresh buffer. */
  needsSessionRefresh(): boolean {
    const id = localStorage.getItem(AUTH_STORAGE_KEYS.idToken);
    const access = localStorage.getItem(AUTH_STORAGE_KEYS.accessToken);
    const isValid = (t: string) => this.isTokenValid(t);
    return (
      isTokenExpiringSoon(id, isValid) || isTokenExpiringSoon(access, isValid)
    );
  }

  /**
   * Silently refresh Cognito tokens when needed (deduped).
   * No-op when tokens are still fresh.
   */
  ensureFreshSession(): Observable<void> {
    const refreshToken = localStorage.getItem(AUTH_STORAGE_KEYS.refreshToken);
    if (!refreshToken) {
      return throwError(() => new Error('No refresh token'));
    }
    if (!this.needsSessionRefresh()) {
      return of(undefined);
    }
    return this.refreshSession().pipe(
      switchMap((output) => {
        const result = output.AuthenticationResult;
        if (!result || !this.saveSession(result)) {
          return throwError(
            () => new Error('Failed to save refreshed session'),
          );
        }
        return of(undefined);
      }),
    );
  }

  saveSession(authResult: AuthenticationResultType): boolean {
    const accessToken = authResult.AccessToken;
    const idToken = authResult.IdToken;
    if (!accessToken || !idToken) {
      return false;
    }
    const refreshToken =
      authResult.RefreshToken ??
      localStorage.getItem(AUTH_STORAGE_KEYS.refreshToken) ??
      '';
    if (!refreshToken) {
      return false;
    }
    localStorage.setItem(AUTH_STORAGE_KEYS.accessToken, accessToken);
    localStorage.setItem(AUTH_STORAGE_KEYS.idToken, idToken);
    localStorage.setItem(AUTH_STORAGE_KEYS.refreshToken, refreshToken);
    return true;
  }

  clearSession(): void {
    localStorage.removeItem(AUTH_STORAGE_KEYS.accessToken);
    localStorage.removeItem(AUTH_STORAGE_KEYS.idToken);
    localStorage.removeItem(AUTH_STORAGE_KEYS.refreshToken);
    localStorage.removeItem(AUTH_STORAGE_KEYS.requestedUrl);
  }

  signUp(
    username: string,
    phoneNumber: string,
    email: string,
    password: string,
    givenName: string,
    userType: UserTypeEnum,
  ) {
    const command = new SignUpCommand({
      ClientId: this.configService.clientId,
      Username: username,
      Password: password,
      UserAttributes: [
        {
          Name: 'email',
          Value: email,
        },
        {
          Name: 'custom:user_type',
          Value: userType,
        },
        {
          Name: 'given_name',
          Value: givenName,
        },
        {
          Name: 'phone_number',
          Value: phoneNumber,
        },
      ],
    });

    return from(this.client.send(command));
  }

  acceptTerms(hash: string) {
    const endpoint = `${this.configService.baseApi}/shared/accept-terms`;
    return this.http.post<TermsAndConditionsResponse>(endpoint, {
      terms_hash: hash,
    });
  }
  getTermsPresignedUrl() {
    const endpoint = `${this.configService.baseApi}/shared/upload-terms`;
    return this.http.post<TermsAndConditionsPresignedUrl>(endpoint, {});
  }

  updateTermsCommand() {
    const endpoint = `${this.configService.baseApi}/shared/upload-terms/checksum`;
    return this.http.post<TermsAndConditionsPresignedUrl>(endpoint, {});
  }

  getTermsHash() {
    const endpoint = `${this.configService.baseApi}/shared/upload-terms/checksum`;
    return this.http.get<TermsAndConditionsChecksum>(endpoint);
  }

  confirmSignup(username: string, confirmationCode: string) {
    const command = new ConfirmSignUpCommand({
      ClientId: this.configService.clientId,
      Username: username,
      ConfirmationCode: confirmationCode,
    });
    return from(this.client.send(command));
  }

  getUserInfo(accessToken: string | undefined) {
    const command = new GetUserCommand({
      AccessToken: accessToken,
    });
    return from(this.client.send(command));
  }

  respondToNewPasswordChallenge(username: string, newPassword: string) {
    const command = new RespondToAuthChallengeCommand({
      ClientId: this.configService.clientId,
      ChallengeName: 'NEW_PASSWORD_REQUIRED',
      Session: this.newPasswordChallengSession(),
      ChallengeResponses: {
        USERNAME: username,
        NEW_PASSWORD: newPassword,
      },
    });

    return from(this.client.send(command));
  }
  changePassword(currentPassword: string, newPassword: string) {
    // return from(
    //   updatePassword({ oldPassword: currentPassword, newPassword })
    // ).pipe(
    //   catchError((err) => {
    //     console.error('Error updating password:', err);
    //     return throwError(() => err);
    //   })
    // );
  }
  updateUserAttrs(userAttributes: Record<string, any>) {
    // return from(
    //   updateUserAttributes({
    //     userAttributes: userAttributes,
    //   })
    // ).pipe(
    //   catchError((err) => {
    //     console.error('Error updating user attributes:', err);
    //     return throwError(() => err);
    //   })
    // );
  }
  authUserFromAttr(attrs: AttributeType[]) {
    const userObject: AuthUser = attrs.reduce((acc, { Name, Value }) => {
      // Ensure Name is a valid string before using it as a key
      if (typeof Name === 'string') {
        // You can add checks here if you want to validate which fields you expect
        switch (Name) {
          case 'sub':
            acc.sub = Value ?? '';
            break;
          case 'email':
            acc.email = Value ?? '';
            break;
          case 'email_verified':
            acc.email_verified = Value ?? '';
            break;
          case 'phone_number':
            acc.phone_number = Value ?? '';
            break;
          case 'phone_number_verified':
            acc.phone_number_verified = Value ?? '';
            break;
          case 'family_name':
            acc.family_name = Value ?? '';
            break;
          case 'given_name':
            acc.given_name = Value ?? '';
            break;
          case 'address':
            acc.address = Value ?? '';
            break;
          case 'custom:hq_country':
            acc['custom:hq_country'] = Value ?? '';
            break;
          case 'custom:operating_countries':
            acc['custom:operating_countries'] = Value ?? '';
            break;
          case 'custom:userObject':
            acc['custom:user_type'] = Value ?? '';
            break;
          case 'custom:parent_sub':
            acc['custom:parent_sub'] = Value ?? '';
            break;
          case 'custom:user_type':
            acc['custom:user_type'] = Value ?? '';
            break;
          case 'custom:terms_hash':
            acc['custom:terms_hash'] = Value ?? '';
            break;
          // Add more cases for other expected attributes
          default:
            // Handle any unexpected attributes if needed
            break;
        }
      }
      return acc;
    }, {} as AuthUser);
    return userObject;
  }
}
